import { useState } from 'react';
import { Navigate } from 'react-router-dom';
import { Form, Input, Button, Card, App, Select, Typography, Space, Result } from 'antd';
import { IdcardOutlined, GlobalOutlined, HomeOutlined, MailOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../contexts/AuthContext';
import BrandLogo from '../../components/common/BrandLogo';
import LocationFormSection from '../../components/auth/LocationFormSection';
import { savePendingRegistration, sendSignInLinkForRegistration } from '../../api/auth';

const { Title } = Typography;
const { Option } = Select;

export type RegisterLocationState = {
  apartmentName?: string;
  country?: string;
  region?: string;
};

export const Register = () => {
  const [loading, setLoading] = useState(false);
  const [emailSent, setEmailSent] = useState(false);
  const { t, i18n } = useTranslation(['auth', 'common']);
  const { firebaseUser } = useAuth();
  const { message } = App.useApp();
  const [form] = Form.useForm();

  if (firebaseUser) {
    return <Navigate to="/select-workspace" replace />;
  }

  const onFinish = async (values: {
    apartmentName: string;
    country: string;
    region: string;
    fullName: string;
    email: string;
  }) => {
    try {
      setLoading(true);
      await savePendingRegistration(
        values.email,
        values.fullName,
        values.apartmentName,
        values.country,
        values.region
      );
      await sendSignInLinkForRegistration(values.email);
      setEmailSent(true);
      message.success(t('auth:registerEmailSent'));
      console.info('[Convive Auth] Register: pending registration saved and sign-in link send completed');
    } catch (error: unknown) {
      console.error('[Convive Auth] Register failed (pending registration and/or sendSignInLinkToEmail):', error);
      const err = error as { code?: string; message?: string };
      const code = err?.code;
      const msg = err?.message ?? '';
      if (code === 'auth/email-already-in-use') {
        message.error(t('auth:registerEmailInUse'));
      } else if (code === 'auth/invalid-email') {
        message.error(t('auth:emailInvalid'));
      } else if (code === 'auth/operation-not-allowed') {
        message.error(t('auth:emailLinkNotEnabled'));
      } else if (code === 'permission-denied' || code?.includes('permission')) {
        message.error(t('auth:registerPermissionDenied'));
      } else {
        message.error(msg || t('auth:registerError'));
      }
    } finally {
      setLoading(false);
    }
  };

  const handleLanguageChange = (value: string) => {
    i18n.changeLanguage(value);
  };

  return (
    <div
      style={{
        minHeight: '100vh',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        background: '#f0f2f5',
      }}
    >
      <Space direction="vertical" size="small" style={{ width: '100%', maxWidth: 480, textAlign: 'center' }}>
        <Card style={{ boxShadow: '0 4px 8px rgba(0,0,0,0.1)' }}>
          <div style={{ textAlign: 'center', marginBottom: 24 }}>
            <BrandLogo variant="bright" showTagline={true} />
          </div>
          <Title level={4} style={{ margin: 0, fontWeight: 'normal', color: '#666' }}>
            {t('auth:registerTitle')}
          </Title>
          <br />
          {emailSent ? (
            <Result
              status="success"
              title={t('auth:checkEmailTitle')}
              subTitle={t('auth:checkEmailSubtitle')}
              extra={
                <Button type="primary" href="/login">
                  {t('auth:backToLogin')}
                </Button>
              }
            />
          ) : (
            <Form
              form={form}
              name="register"
              onFinish={onFinish}
              layout="vertical"
              size="large"
              initialValues={{ country: 'CL' }}
            >
              <Form.Item
                name="apartmentName"
                label={t('auth:apartmentName')}
                rules={[{ required: true, message: t('auth:apartmentNameRequired') }]}
              >
                <Input
                  prefix={<HomeOutlined />}
                  placeholder={t('auth:apartmentNamePlaceholder')}
                />
              </Form.Item>

              <LocationFormSection t={t} />

              <Form.Item
                name="fullName"
                label={t('auth:fullName')}
                rules={[{ required: true, message: t('auth:fullNameRequired') }]}
              >
                <Input
                  prefix={<IdcardOutlined />}
                  placeholder={t('auth:fullNamePlaceholder')}
                />
              </Form.Item>

              <Form.Item
                name="email"
                label={t('auth:email')}
                rules={[
                  { required: true, message: t('auth:emailRequired') },
                  { type: 'email', message: t('auth:emailInvalid') },
                ]}
              >
                <Input
                  prefix={<MailOutlined />}
                  placeholder={t('auth:emailPlaceholder')}
                />
              </Form.Item>

              <Form.Item>
                <Button type="primary" htmlType="submit" loading={loading} block icon={<MailOutlined />}>
                  {t('auth:sendVerificationEmail')}
                </Button>
              </Form.Item>
            </Form>
          )}

          <div style={{ marginTop: 16 }}>
            <a href="/login">{t('auth:alreadyHaveAccount')}</a>
          </div>
        </Card>

        <div>
          <GlobalOutlined style={{ marginRight: 8 }} />
          <Select
            value={i18n.language}
            onChange={handleLanguageChange}
            style={{ width: 140 }}
          >
            <Option value="en">English</Option>
            <Option value="es">Español</Option>
            <Option value="pt">Português</Option>
          </Select>
        </div>
      </Space>
    </div>
  );
};
